import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

//CSS
import "./packagePage.css";
import "../0PageStyles.css";

//Components
import Title from "../../Components/Other Components/Title";
import OfferView from "Components/Home Components/OfferView";
import Button from "Components/Other Components/button";
import PageLoader from "Components/Other Components/pageLoader";
import PopupConfirmation from "Components/Other Components/popupConfirmation";

//Function
import { UpdateRegisterStage } from "BackendAccess/UserHooks";
import {
  GetLemonSqueezyProducts,
  GetProductsWithSubscriptionContext,
  InitiatePurchase,
  CancelUserSubscription,
  ResumeUserSubscription,
} from "BackendAccess/LemonSqeezyHooks";

import { useErrorHandler } from "hooks/errorHandler";
import { useNotification } from "Contexts/NotificationContext";
import { usePopup } from "Contexts/PopupContext";

//Animation
import PageWrapper from "../pageWrapper";

const productActiveStates = ["on_trial", "active", "paid"];

export default function PackagePage() {
  const navigate = useNavigate();
  const { t } = useTranslation("home");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const [userHasPackage, setUserHasPackage] = useState(false);
  const [LSQProducts, setLSQProducts] = useState([]);
  const [userPackage, setUserPackage] = useState(null);

  const { showPopup, removePopup } = usePopup();

  useEffect(() => {
    fetchLSQProducts();
  }, []);

  const refreshPage = () => {
    window.location.reload();
  };

  const checkIfUserHasPackage = (products) => {
    return (
      products?.userSubscription &&
      (products?.userSubscription[0]?.subscription?.attributes?.status !==
        "expired" ||
        products?.userSubscription[0].isLifetime)
    );
  };

  const fetchLSQProducts = async () => {
    try {
      setLoading(true);
      const [products, error] = await GetLemonSqueezyProducts();
      const [advancedProducts, errorAdvanced] =
        await GetProductsWithSubscriptionContext();
      console.log("Advanced products", advancedProducts);
      if (advancedProducts) {
        let sortedProducts = advancedProducts;
        sortedProducts.products = sortProducts(advancedProducts.products);
        setLSQProducts(sortedProducts);
        const userHasPckg = checkIfUserHasPackage(sortedProducts);
        setUserHasPackage(userHasPckg);
        if (!userHasPckg) {
          setUserPackage({
            lifetime: false,
            status: "no_package",
            packageId: -1,
            expires: "",
            modifyUrl: null,
          });
          return;
        }
        if (sortedProducts?.userSubscription[0]?.isLifetime) {
          setUserPackage({
            lifetime: true,
            status:
              sortedProducts?.userSubscription[0]?.subscription?.attributes
                ?.status,
            packageId:
              sortedProducts?.userSubscription[0]?.subscription?.attributes
                ?.first_order_item?.product_id,
            expires: t("packages.expiresNever"),
            modifyUrl: null,
          });
        } else {
          const expires =
            sortedProducts?.userSubscription[0]?.subscription?.attributes
              ?.ends_at ??
            sortedProducts?.userSubscription[0]?.subscription?.attributes
              ?.trial_ends_at;
          const expiresDate = expires.split("T")[0];
          setUserPackage({
            lifetime: false,
            status:
              sortedProducts?.userSubscription[0]?.subscription?.attributes
                ?.status,
            packageId:
              sortedProducts?.userSubscription[0]?.subscription?.attributes
                ?.product_id,
            expires: expiresDate,
            modifyUrl:
              sortedProducts?.userSubscription[0]?.subscription?.attributes
                ?.urls?.update_payment_method,
          });
        }
      } else {
        console.error("Failed to fetch LemonSqueezy products.");
      }
    } catch (error) {
      console.error("Error fetching LemonSqueezy products:", error);
      setError(error);
    } finally {
    }
  };

  useEffect(() => {
    if (userHasPackage && userPackage) setLoading(false);
  }, [userHasPackage, userPackage]);

  const sortProducts = (products) => {
    const orderIndex = {};
    cardOrder.forEach((id, index) => {
      orderIndex[id] = index;
    });
    console.log("Sorting products ", products, cardOrder, orderIndex);
    const sortedProducts = products.sort((a, b) => {
      const indexA = orderIndex[a.id];
      const indexB = orderIndex[b.id];

      return indexA - indexB;
    });
    return sortedProducts;
  };

  const handlePurchaseClick = async (productId) => {
    const [data, error] = await InitiatePurchase(productId);
    if (!error) {
      if (!data.resumed)
        window.location.href =
          LSQProducts.products.find((item) => item.id === productId).attributes
            .buy_now_url || "/";
    } else {
      setError(error);
      console.log("Purchase error", error);
    }
  };

  const mapProductsToCardData = useCallback(
    (product) => {
      if (!userPackage || !userHasPackage) return;
      const productSelected =
        userPackage.status !== "expired" &&
        parseInt(userPackage.packageId) === parseInt(product.id);
      const productActive = productActiveStates.includes(userPackage.status);
      const lifetimeActive = userPackage.lifetime;

      const getProductState = () => {
        if (!userHasPackage) return "NoPackage";
        if (productSelected && productActive) return "Active";
        if (productSelected) return "Paused";
        if (lifetimeActive && !productSelected) return "Unavailable";
        return "NotSelected";
      };

      const cardStateProps = {
        NoPackage: {
          btnText: t("packages.get"),
          onClick: () => {
            handlePurchaseClick(product.id);
          },
        },
        Unavailable: {
          btnText: t("packages.unavailable"),
          onClick: () => {
            showNotification(t("packages.packageUnavailable"), "info");
          },
        },
        Active: {
          btnText: t("packages.current"),
          onClick: () => {
            showNotification(t("packages.PackageOwned"), "info");
          },
        },
        Paused: {
          btnText: t("packages.resume"),
          onClick: async () => {
            await resumeSubscription();
          },
        },
        NotSelected: {
          btnText: t("packages.switch"),
          onClick: async () => {
            setLoading(true);
            showNotification(t("packages.switchingPackage"), "info");
            await handlePurchaseClick(product.id);

            await fetchLSQProducts();
            showNotification(t("packages.pausedExists"), "success");
          },
        },
      };

      const productState = getProductState();

      const pausedInfo = {
        message: productActive
          ? t("packages.packageIsActive")
          : t("packages.packageIsPaused"),
        expires:
          (productActive
            ? t("packages.nextPayment")
            : t("packages.packageExpires")) +
          " " +
          userPackage.expires,
        expiring: !productActive,
      };
      const priceFormatedSplit =
        product?.attributes?.price_formatted?.split("/");
      const finalPrice = priceFormatedSplit[0];
      const paymentType =
        priceFormatedSplit.length > 1
          ? "/" + priceFormatedSplit[1]
          : "/one time";

      return {
        title: product?.attributes?.name || t("packages.basic.title"),
        price: finalPrice,
        paymentType: paymentType,
        pausedInfo: pausedInfo,
        selected: productSelected,
        btnDisabled: lifetimeActive,
        btnText: cardStateProps[productState]["btnText"],
        ...cardProps[product.id],

        onClick: cardStateProps[productState]["onClick"],
      };
    },
    [userHasPackage, userPackage]
  );
  const resumeSubscription = async () => {
    try {
      showNotification(t("packages.resumingSubscription"), "info");

      setLoading(true);
      const [_, error] = await ResumeUserSubscription();
      if (error) setError(error);
      await fetchLSQProducts();
      showNotification(t("packages.subscriptionResumed"), "success");
    } catch (err) {
      setError(err);
    }
  };
  const buttonSection = useCallback(() => {
    if (!userPackage || !userHasPackage)
      return (
        <div className="btn-container">
          <Button
            className="mt-3"
            btnSize={"btn-large"}
            btnStyle={"btn-default"}
            onClick={onGuestClick}
          >
            {t("packages.guest")}
          </Button>
        </div>
      );
    if (userPackage.lifetime) return null;
    if (userHasPackage && productActiveStates.includes(userPackage.status)) {
      return (
        <div className="btn-container">
          <Button
            className="mt-3"
            btnSize={"btn-large"}
            btnStyle={"btn-negative"}
            onClick={onGuestClick}
          >
            {t("packages.cancel")}
          </Button>
          <Button
            className="mt-3"
            btnSize={"btn-large"}
            btnStyle={"btn-default"}
            onClick={onModifyClick}
          >
            {t("packages.modify")}
          </Button>
        </div>
      );
    }
    return;
  }, [userHasPackage, userPackage]);

  const onModifyClick = () => {
    setLoading(true);
    window.location.href = userPackage.modifyUrl;
    setLoading(false);
  };

  //PACKAGES***********************************
  const cardOrder = [
    process.env.REACT_APP_LS_PRODUCT_ID_BASIC,
    process.env.REACT_APP_LS_PRODUCT_ID_PRO,
    process.env.REACT_APP_LS_PRODUCT_ID_PRO_PLUS,
    process.env.REACT_APP_LS_PRODUCT_ID_LIFETIME,
  ];
  const cardProps = {
    //Basic
    [process.env.REACT_APP_LS_PRODUCT_ID_BASIC]: {
      props: {
        [t("packages.props.warehouseCount")]: 1,
        [t("packages.props.itemCount")]: 5000,
        [t("packages.props.tagCount")]: 100,
        /*         [t("packages.props.trafficData")]: false,
         */ [t("packages.props.employeeManagement")]: false,
        [t("packages.props.roleManagement")]: false,
        [t("packages.props.supportLevel")]: t("packages.basic.supportLevel"),
      },
      color: "var(--CARDCOLOR1)",
    },
    //Pro
    [process.env.REACT_APP_LS_PRODUCT_ID_PRO]: {
      props: {
        [t("packages.props.warehouseCount")]: 5,
        [t("packages.props.itemCount")]: 10000,
        [t("packages.props.tagCount")]: 1000,
        /*         [t("packages.props.trafficData")]: t("packages.pro.trafficData"),
         */ [t("packages.props.employeeManagement")]: true,
        [t("packages.props.employeeCount")]: 100,
        [t("packages.props.roleManagement")]: true,
        [t("packages.props.roleCount")]: 50,
        [t("packages.props.supportLevel")]: t("packages.pro.supportLevel"),
      },
      color: "var(--CARDCOLOR2)",
    },
    //Pro+
    [process.env.REACT_APP_LS_PRODUCT_ID_PRO_PLUS]: {
      props: {
        [t("packages.props.warehouseCount")]: 50,
        [t("packages.props.itemCount")]: 50000,
        [t("packages.props.tagCount")]: t("packages.props.unlimited"),
        /*         [t("packages.props.trafficData")]: t("packages.proPlus.trafficData"),
         */ [t("packages.props.employeeManagement")]: true,
        [t("packages.props.employeeCount")]: 1000,
        [t("packages.props.roleManagement")]: true,
        [t("packages.props.roleCount")]: 500,
        [t("packages.props.supportLevel")]: t("packages.proPlus.supportLevel"),
      },
      color: "var(--CARDCOLOR3)",
    },
    //Lifetime
    [process.env.REACT_APP_LS_PRODUCT_ID_LIFETIME]: {
      props: {
        [t("packages.props.warehouseCount")]: t("packages.props.unlimited"),
        [t("packages.props.itemCount")]: t("packages.props.unlimited"),
        [t("packages.props.tagCount")]: t("packages.props.unlimited"),
        /*         [t("packages.props.trafficData")]: t("packages.lifetime.trafficData"),
         */ [t("packages.props.employeeManagement")]: true,
        [t("packages.props.employeeCount")]: t("packages.props.unlimited"),
        [t("packages.props.roleManagement")]: true,
        [t("packages.props.roleCount")]: t("packages.props.unlimited"),
        [t("packages.props.supportLevel")]: t("packages.lifetime.supportLevel"),
      },
      color: "var(--CARDCOLOR4)",
    },
  };
  //PACKAGES END***************************************

  const onGuestClick = async () => {
    if (!userHasPackage) {
      try {
        setLoading(true);
        const [_, error] = await UpdateRegisterStage(4);
        if (error) setError(error);
        showNotification(t("packages.Canceled"), "success");
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        navigate("/workspace/messages");
      }
    } else {
      canclePackageClick();
    }
  };

  const canclePackageClick = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to cancel your package?", {
              ns: "settings",
            })}
            <br />
            {t(
              "This will result in you and all of your employees losing the permissions to perform any actions or view any data.",
              {
                ns: "settings",
              }
            )}
            <br />
            {t(
              "Your company and data will be deleted after 1 month if the package is not renewed.",
              {
                ns: "settings",
              }
            )}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => {
          cancelUserPackage();
          removePopup(key);
        }}
        onCancel={() => {
          removePopup(key);
        }}
        highSecurity={true}
      />,
      key
    );
  };

  const cancelUserPackage = async () => {
    try {
      setLoading(true);
      const [_, error] = await CancelUserSubscription();
      if (error) {
        setError(error);
      }
      await fetchLSQProducts();
      showNotification(t("packages.subscriptionCanceled"), "info");
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const tutorialSteps = [
    {
      target: null,
      title: t("packages.tutorial.intro.title"),
      message: t("packages.tutorial.intro.message"),
    },
    {
      target: ".offer-cards-container",
      title: t("packages.tutorial.cards.title"),
      message: t("packages.tutorial.cards.message"),
    },
    {
      target: ".btn-container",
      title: t("packages.tutorial.actions.title"),
      message: t("packages.tutorial.actions.message"),
    },
  ];

  return (
    <PageWrapper tutorialId="package" tutorialSteps={tutorialSteps}>
      <div className="page-content">
        <PageLoader loading={loading} error={pageError}>
          <div id="PackageSection" className="packageSection">
            <Title className="w-90 mt-3">{t("sections.packages")}</Title>

            <OfferView
              cards={LSQProducts?.products?.map(mapProductsToCardData)}
            />
            {buttonSection()}
          </div>
        </PageLoader>
      </div>
    </PageWrapper>
  );
}
